import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import "../styles/ConsultationOptions.css";
import LeafSVG from "assets/svg/LeafSVG";
import VideoSVG from "assets/svg/VideoSVG";
import CheckSVG from "assets/svg/CheckSVG";
import { getSavedCardsApi, getAIRequestApi } from "services/getApis";
import { orderHerbsApi } from "services/postApis";
import { ErrorHandler } from "utils/toast";
import ButtonLoader from "components/ButtonLoader";

// ── Static pricing / feature data ──────────────────────────────
const OPTIONS = {
  pathA: {
    id: "path-a",
    label: "Path A",
    title: "Custom Herbal Recommendation",
    icon: null,
    IconComponent: LeafSVG,
    iconColor: "#16a34a",
    accentClass: "co-card-accent--a",
    pathLabelClass: "co-path-label--a",
    price: "$179",
    priceNote: "$99 consultation fee if you decline the formula",
    description:
      "Our practitioner reviews your intake and prepares a personalised herbal formula based on your health profile. You decide whether to proceed after seeing the recommendation.",
    features: [
      "Practitioner intake review",
      "Personalised herbal recommendation",
      "Approve ($179) or decline ($99) after review",
      "Herbal formula preparation & tracked shipping",
      "72-hour response window",
    ],
  },
  pathB: {
    id: "path-b",
    label: "Path B",
    title: "Live Zoom Consultation",
    icon: null,
    IconComponent: VideoSVG,
    iconColor: "#16a34a",
    accentClass: "co-card-accent--b",
    pathLabelClass: "co-path-label--b",
    price: "$275",
    priceNote: "All-inclusive — charged upon recommendation finalization",
    description:
      "A live Zoom session with your practitioner who reviews your intake, conducts the consultation, and finalises your personalised herbal recommendation and formula.",
    features: [
      "Live Zoom consultation",
      "Practitioner intake review",
      "Personalised herbal recommendation",
      "Herbal formula preparation & shipping",
    ],
  },
};

const ConsultationOptions = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const userSubmissionId = location.state?.userSubmissionId;
  const passedResponseData = location.state?.responseData;
  const [selectedPath, setSelectedPath] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { data: aiData } = useQuery({
    queryKey: ["getHerbalPlan", userSubmissionId],
    queryFn: () => getAIRequestApi(userSubmissionId),
    enabled: !!userSubmissionId && !passedResponseData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const responseData = passedResponseData || aiData?.payload;

  const handleContinue = async () => {
    if (!selectedPath || isLoading) return;

    let savedCards = [];
    try {
      setIsLoading(true);

      // Place order for selected pathway (Path A or Path B)
      const orderData = {
        userSubmissionId,
        items:
          responseData?.recommended_herbs?.map((herb) => ({ herbId: herb.id })) ||
          [],
        explanation: responseData?.explanation || "",
      };

      await orderHerbsApi(orderData);

      const res = await getSavedCardsApi();
      savedCards = res?.payload || res?.data || res || [];

      navigate("/payment-authorization", {
        state: {
          userSubmissionId,
          selectedPath,
          pathLabel: OPTIONS[selectedPath].label,
          pathTitle: OPTIONS[selectedPath].title,
          price: OPTIONS[selectedPath].price,
          priceNote: OPTIONS[selectedPath].priceNote,
          initialSavedCards: Array.isArray(savedCards) ? savedCards : [],
        },
      });
    } catch (error) {
      console.log("Error during pathway selection:", error);
      ErrorHandler(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-content co-wide-content">
        <div >
          {/* Header */}
          <div className="co-header">
            <h1 className="co-heading">Choose your consultation pathway.</h1>
            <p className="co-subheading">
              Select the option that best suits your needs. Your payment
              information will be collected next, but{" "}
              <strong>your card will not be charged</strong> until after the
              practitioner reviews your intake.
            </p>
          </div>

          {/* Option cards */}
          <div className="co-cards-grid">
            {Object.entries(OPTIONS).map(([key, opt]) => (
              <div
                key={opt.id}
                id={`co-${opt.id}-card`}
                className={`co-card ${selectedPath === key ? "co-card--selected" : ""}`}
                onClick={() => setSelectedPath(key)}
                role="radio"
                aria-checked={selectedPath === key}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setSelectedPath(key);
                }}
              >
                {/* Radio indicator */}
                <div className="co-radio">
                  <div className="co-radio-dot" />
                </div>

                {/* Icon */}
                <div className="co-card-icon">
                  <opt.IconComponent size={opt.id === "path-b" ? 34 : 26} color={opt.iconColor} />
                </div>

                {/* Path pill */}
                <span className={`co-path-pill ${opt.pathLabelClass}`}>
                  {opt.label}
                </span>

                {/* Title */}
                <h2 className="co-card-title">{opt.title}</h2>

                {/* Price */}
                <p className="co-card-price">{opt.price}</p>
                <p className="co-card-price-note">{opt.priceNote}</p>

                {/* Description */}
                <p className="co-card-desc">{opt.description}</p>

                {/* Features */}
                <ul className="co-card-features">
                  {opt.features.map((feat, i) => (
                    <li key={i}>
                      <span className="co-feature-check" aria-hidden="true">
                        <CheckSVG size={13} color="#16a34a" />
                      </span>
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="co-cta">
            <button
              id="co-continue-btn"
              className="approval-btn primary"
              onClick={handleContinue}
              disabled={!selectedPath || isLoading}
            >
              {isLoading ? (
                <ButtonLoader />
              ) : selectedPath ? (
                `Continue with ${OPTIONS[selectedPath].label} — ${OPTIONS[selectedPath].price}`
              ) : (
                "Select a pathway to continue"
              )}
            </button>
            <p className="co-cta-note">
              <svg width="18" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "inline", verticalAlign: "middle", marginRight: 5, marginBottom: 2 }}>
                <rect x="5" y="11" width="14" height="10" rx="2.5" fill="#9ca3af" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              No charge made at this stage. You can review before any payment is processed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationOptions;
